import * as React from 'react';
import { connect } from 'react-redux';
import { camelCase, isEqual } from 'lodash';

import DeliverableMoveContent from './DeliverableMoveContent';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { getClientApprovalMedia } from 'modules/deliverables/reducers/deliverableContentApproval';
import { moveContent } from 'modules/subcampaigns/reducers/offerDeliverables';

import { DELIVERABLE_ICONS, DELIVERABLE_TAG_COLORS_MAP } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./deliverable-move-content.i18n.json'));

import {
  Deliverable,
  DeliverableAttachment,
  DeliverableClientApproval,
  DeliverableClientApprovalItem,
  DeliverableShort,
  MixedSelectDropdownItem,
  MixedSelectDropdownTag,
  Subcampaign
} from 'types';

type OwnProps = {
  attachments: Array<DeliverableAttachment>,
  deliverable: Deliverable,
  subcampaign: Subcampaign,
  deliverablesWithCA2: Array<DeliverableShort>,
  setDeliverableModified: (isModified: boolean) => void,
  onSave: () => void,
  onOpenMoveContent: (showMoveContent: boolean) => void
};

type StateProps = {
  releasedContent: Array<DeliverableClientApprovalItem>
};

type DispatchProps = {
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message: string,
      confirmButtonLabel?: string,
      cancelButtonLabel?: string,
      onConfirm?: () => void,
      onCancel?: () => void,
      onClose?: () => void,
      innerContent?: React.ReactNode
    }
  ) => void,
  dispatchMoveContent: (
    offerId: number,
    deliverableId: number,
    targetDeliverableId: number,
    assetIds: Array<string | number>
  ) => Promise<void>,
  dispatchHideConfirmationModal: () => void,
  dispatchGetClientApprovalMedia: (offerId: number, deliverableId: number) => Promise<DeliverableClientApproval>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  contentAttachments: Array<DeliverableAttachment>,
  selectedAttachmentsIds: Array<string | number>,
  selectedDeliverableId: number | null,
  isModified: boolean
};

const msgKey = 'deliverable_move_content';

class DeliverableMoveContentAttachments extends React.PureComponent<Props, State> {
  state: State = {
    contentAttachments: [],
    selectedAttachmentsIds: [],
    selectedDeliverableId: null,
    isModified: false
  };

  componentDidMount() {
    const { dispatchGetClientApprovalMedia, deliverable, subcampaign } = this.props;
    const { id } = deliverable;
    const { offer } = subcampaign;

    dispatchGetClientApprovalMedia(offer.id, id)
      .then(() => this.setInitialContentAttachments());
  }

  componentDidUpdate(prevProps: Props) {
    const {
      attachments,
      releasedContent,
      deliverable,
      subcampaign,
      dispatchGetClientApprovalMedia
    } = this.props;
    const { offer } = subcampaign;

    if (!isEqual(prevProps.releasedContent, releasedContent)) {
      this.setInitialContentAttachments();
    }

    if (!isEqual(prevProps.attachments, attachments)) {
      dispatchGetClientApprovalMedia(offer.id, deliverable.id)
        .then(() => this.setInitialContentAttachments());
    }
  }

  setInitialContentAttachments = () => {
    const { attachments, releasedContent } = this.props;

    const contentAttachments = attachments.map(attachment => {
      const releasedItem = releasedContent.find(a => a.assetId === attachment.id);

      return {
        ...attachment,
        approvalStatus: releasedItem?.status || 'pending'
      };
    });

    this.setState({ contentAttachments });
  };

  getMenuItems(deliverablesWithCA2: Array<DeliverableShort>, selectedDeliverableId: number | null): Array<MixedSelectDropdownItem> {
    const { deliverable } = this.props;

    return deliverablesWithCA2.filter(d => d.id !== deliverable.id).map(d => {
      const dropdownTag = (d.tag?.name && d.tag?.color) ? ({
        label: d.tag.name,
        color: DELIVERABLE_TAG_COLORS_MAP[d.tag.color!]
      } as MixedSelectDropdownTag) : undefined;

      return ({
        id: d.id,
        identifier: String(d.id),
        value: String(d.id),
        label: d.name,
        isDisabled: false,
        isFocused: false,
        isSelected: String(d.id) === String(selectedDeliverableId),
        icon: {
          primary: { name: DELIVERABLE_ICONS[camelCase(d.type)] || 'instagram-custom-fg' }
        },
        tag: {
          secondary: dropdownTag
        }
      });
    });
  }

  getSelectedAttachments = () => {
    const { contentAttachments, selectedAttachmentsIds } = this.state;

    return contentAttachments.map(attachment => {
      const selectedItem = selectedAttachmentsIds.find(id => id === attachment.id);

      return {
        ...attachment,
        isSelected: Boolean(selectedItem)
      };
    });
  };

  selectAttachment = (isSelected: boolean, deliverableAttachmentId: string | number) => {
    if (isSelected) {
      this.setState(state => ({
        selectedAttachmentsIds: [...state.selectedAttachmentsIds, deliverableAttachmentId]
      }));
      this.handleModification(true);
      return;
    }

    this.setState(state => ({
      selectedAttachmentsIds: state.selectedAttachmentsIds.filter(id => id !== deliverableAttachmentId)
    }));
  };

  handleModification = (isModified: boolean) => {
    const { setDeliverableModified } = this.props;
    this.setState({ isModified });
    setDeliverableModified(isModified);
  };

  handleChangeSelectedDeliverable = (
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ) => {
    const value = selectedItem?.identifier;
    this.setState({ selectedDeliverableId: value ? Number(value) : null });
    this.handleModification(true);
  };

  handleSave = async () => {
    const { deliverable, subcampaign, dispatchMoveContent, onSave } = this.props;
    const { selectedAttachmentsIds, selectedDeliverableId } = this.state;
    const { offer } = subcampaign;

    if (selectedDeliverableId) {
      await dispatchMoveContent(offer.id, deliverable.id, selectedDeliverableId, selectedAttachmentsIds);
      onSave();
    }
  };

  handleCancel = () => {
    this.setState({
      selectedAttachmentsIds: [],
      selectedDeliverableId: null
    });

    this.handleModification(false);
  };

  notifyAboutModifyingClientPage = (cb) => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.move_content_modal.title`),
      message: __t(`${msgKey}.move_content_modal.message`),
      confirmButtonLabel: __t(`${msgKey}.move_content_modal.confirm_btn`),
      onClose: () => dispatchHideConfirmationModal(),
      onCancel: () => dispatchHideConfirmationModal(),
      onConfirm: () => {
        cb();

        dispatchHideConfirmationModal();
      },
      innerContent: (
        <div className="deliverable-client-approval__modal-confirmation">
          {__t(`${msgKey}.move_content_modal.confirmation_msg`)}
        </div>
      )
    });
  };

  handleAttachmentsSelectionChange = (
    isSelected: boolean,
    deliverableAttachmentId?: number | string,
    event?: React.MouseEvent
  ) => {
    event?.preventDefault();

    if (!deliverableAttachmentId) {
      return;
    }

    const { releasedContent } = this.props;
    const isItemSentForApproval = Boolean(releasedContent.find(attachment => attachment.assetId === deliverableAttachmentId));

    if (isSelected && isItemSentForApproval) {
      this.notifyAboutModifyingClientPage(() => {
        this.selectAttachment(isSelected, deliverableAttachmentId);
      });
    } else {
      this.selectAttachment(isSelected, deliverableAttachmentId);
    }
  };

  handleSelectAll = (attachments: Array<DeliverableAttachment>) => {
    this.setState({
      selectedAttachmentsIds: attachments.map(a => a.id)
    });

    this.handleModification(true);
  };

  handleSelectDeselectToggle = (isSelected: boolean): void => {
    const { contentAttachments } = this.state;
    const { releasedContent } = this.props;

    if (isSelected) {
      const attachmentIds = contentAttachments.map(a => a.id);
      const isAnyItemSentForApproval = releasedContent.some(attachment => attachmentIds.some(id => id === attachment.assetId));

      if (isAnyItemSentForApproval) {
        this.notifyAboutModifyingClientPage(() => this.handleSelectAll(contentAttachments));
      } else {
        this.handleSelectAll(contentAttachments);
      }
      return;
    }

    this.setState({
      selectedAttachmentsIds: []
    });
  };

  render() {
    const { selectedAttachmentsIds, selectedDeliverableId, isModified } = this.state;
    const { deliverablesWithCA2, onOpenMoveContent } = this.props;

    return (
      <DeliverableMoveContent
        attachments={this.getSelectedAttachments()}
        isContentSelected={selectedAttachmentsIds.length >= 1}
        selectedDeliverableId={selectedDeliverableId}
        isModified={isModified}
        menuItems={this.getMenuItems(deliverablesWithCA2 || [], selectedDeliverableId)}
        onContentSelectionChange={this.handleAttachmentsSelectionChange}
        onSelectDeselectToggle={this.handleSelectDeselectToggle}
        onSave={this.handleSave}
        onChangeSelectedDeliverable={this.handleChangeSelectedDeliverable}
        onCancel={this.handleCancel}
        onOpenMoveContent={onOpenMoveContent}
      />
    );
  }
}

const mapStateToProps = (state, { deliverable }): StateProps => {
  const { deliverableContentApproval } = state;
  return {
    releasedContent: deliverableContentApproval.clientApprovalData[deliverable.id]?.items || []
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchGetClientApprovalMedia: (offerId: number, deliverableId: number) =>
    dispatch(getClientApprovalMedia(offerId, deliverableId)),
  dispatchMoveContent: (offerId, deliverableId, targetDeliverableId, assetIds) =>
    dispatch(moveContent(offerId, deliverableId, targetDeliverableId, assetIds))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableMoveContentAttachments);
